import type {
  CatalogRef,
  ChatMessage,
  ClientMessage,
  Media,
  Member,
  Playback,
  PlaybackRequest,
  Role,
  ServerMessage,
  Source,
} from '../protocol'
import { getSessionToken } from '../api'
import { t, type MessageKey, type Vars } from '../i18n'

/** Text kept as a message key, so it follows language changes. */
export interface Localized {
  key: MessageKey
  vars?: Vars
}

export type FeedItem =
  | { kind: 'chat'; key: string; msg: ChatMessage }
  | { kind: 'event'; key: string; text: Localized; ts: number }

export interface Reaction {
  key: number
  emoji: string
  nick: string
}

export interface RoomState {
  connection: 'connecting' | 'open' | 'reconnecting' | 'closed'
  closedReason?: 'kicked' | 'deleted' | 'not_found' | 'banned'
  you?: { userId: string; role: Role }
  room?: { id: string; name: string }
  members: Member[]
  playback: Playback
  /** What the room watches according to the catalog, chosen apart from the release. */
  title: CatalogRef | null
  source: Source | null
  media: Media | null
  feed: FeedItem[]
  requests: PlaybackRequest[]
  reactions: Reaction[]
  autoplayNext: boolean
  notice?: { key: number; text: Localized }
}

const initial: RoomState = {
  connection: 'connecting',
  members: [],
  playback: { status: 'paused', pos: 0, anchor: 0, seq: 0 },
  title: null,
  source: null,
  media: null,
  feed: [],
  requests: [],
  reactions: [],
  autoplayNext: true,
}

const FEED_LIMIT = 300

/** Estimates server clock offset from ping round trips. */
class Clock {
  private samples: { rtt: number; offset: number }[] = []
  offset = 0

  add(sent: number, received: number, server: number) {
    const rtt = received - sent
    this.samples.push({ rtt, offset: server - (sent + rtt / 2) })
    if (this.samples.length > 8) this.samples.shift()
    // The fastest round trip has the least asymmetric delay.
    this.offset = this.samples.reduce((a, b) => (b.rtt < a.rtt ? b : a)).offset
  }

  now() {
    return Date.now() + this.offset
  }

  get synced() {
    return this.samples.length >= 3
  }
}

type Listener = () => void

export class RoomClient {
  readonly clock = new Clock()
  private state: RoomState = initial
  private listeners = new Set<Listener>()
  private ws: WebSocket | null = null
  private pingTimer = 0
  private reconnectTimer = 0
  private attempts = 0
  private pingId = 0
  private pings = new Map<number, number>()
  private stopped = false
  private reactionKey = 0
  private noticeKey = 0
  private statusListeners = new Set<(p: Playback) => void>()

  constructor(
    private readonly roomId: string,
    private nick: string,
    private readonly caps: { hevc: boolean; av1: boolean },
  ) {}

  getState = () => this.state

  subscribe = (fn: Listener) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  /** Called synchronously on every playback change, before React renders. */
  onPlayback(fn: (p: Playback) => void) {
    this.statusListeners.add(fn)
    return () => this.statusListeners.delete(fn)
  }

  private set(patch: Partial<RoomState>) {
    this.state = { ...this.state, ...patch }
    this.listeners.forEach((fn) => fn())
  }

  connect() {
    this.stopped = false
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
    // A WebSocket handshake cannot carry the session header (Discord).
    const session = getSessionToken()
    const query = session ? `?session=${encodeURIComponent(session)}` : ''
    const ws = new WebSocket(`${proto}//${location.host}/api/rooms/${this.roomId}/ws${query}`)
    this.ws = ws
    ws.onopen = () => {
      this.attempts = 0
      this.send({ t: 'hello', nick: this.nick, caps: this.caps })
      this.startPings()
    }
    ws.onmessage = (e) => this.handle(JSON.parse(e.data as string) as ServerMessage)
    ws.onclose = () => {
      window.clearInterval(this.pingTimer)
      if (this.ws !== ws) return
      this.ws = null
      if (this.stopped || this.state.closedReason) {
        this.set({ connection: 'closed' })
        return
      }
      this.set({ connection: 'reconnecting' })
      void this.checkRoomThenReconnect()
    }
  }

  private async checkRoomThenReconnect() {
    try {
      const res = await fetch(`/api/rooms/${this.roomId}`)
      if (res.status === 404) {
        this.set({ connection: 'closed', closedReason: 'not_found' })
        return
      }
    } catch {
      // network down; retry below
    }
    const delay = Math.min(10_000, 500 * 2 ** this.attempts++)
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay)
  }

  close() {
    this.stopped = true
    window.clearTimeout(this.reconnectTimer)
    window.clearInterval(this.pingTimer)
    this.ws?.close()
    this.ws = null
  }

  /** Answers a viewer's request; its card goes at once, whatever the server says. */
  answerRequest(id: number, accept: boolean) {
    this.send({ t: 'resolve', reqId: id, accept })
    this.set({ requests: this.state.requests.filter((r) => r.id !== id) })
  }

  send(msg: ClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg))
  }

  setNick(nick: string) {
    this.nick = nick
    this.send({ t: 'set_nick', nick })
  }

  private startPings() {
    window.clearInterval(this.pingTimer)
    let count = 0
    const ping = () => {
      const id = ++this.pingId
      this.pings.set(id, Date.now())
      this.send({ t: 'ping', id })
      count++
      // Sample quickly at first, then keep the estimate fresh.
      if (count === 6) {
        window.clearInterval(this.pingTimer)
        this.pingTimer = window.setInterval(ping, 15_000)
      }
    }
    ping()
    this.pingTimer = window.setInterval(ping, 1_000)
  }

  private event(text: Localized) {
    this.pushFeed({ kind: 'event', key: `e${Date.now()}${Math.random()}`, text, ts: Date.now() })
  }

  private pushFeed(item: FeedItem) {
    const feed = [...this.state.feed, item]
    this.set({ feed: feed.length > FEED_LIMIT ? feed.slice(-FEED_LIMIT) : feed })
  }

  notify(key: MessageKey, vars?: Vars) {
    this.set({ notice: { key: ++this.noticeKey, text: { key, vars } } })
  }

  nickOf(userId: string) {
    return displayNick(this.state.members.find((m) => m.userId === userId)?.nick)
  }

  private handle(msg: ServerMessage) {
    switch (msg.t) {
      case 'welcome':
        this.statusListeners.forEach((fn) => fn(msg.playback))
        this.set({
          connection: 'open',
          you: msg.you,
          room: msg.room,
          members: msg.members,
          playback: msg.playback,
          title: msg.title ?? null,
          source: msg.source,
          media: normalizeMedia(msg.media),
          requests: msg.requests ?? [],
          autoplayNext: msg.autoplayNext,
          feed: msg.chat.map((m) => ({ kind: 'chat', key: `c${m.id}`, msg: m })),
        })
        break
      case 'pong': {
        const sent = this.pings.get(msg.id)
        if (sent !== undefined) {
          this.pings.delete(msg.id)
          this.clock.add(sent, Date.now(), msg.server)
        }
        break
      }
      case 'members':
        this.set({ members: msg.members })
        break
      case 'playback': {
        this.statusListeners.forEach((fn) => fn(msg.playback))
        const patch: Partial<RoomState> = { playback: msg.playback }
        // The prompt is about members; drop it once nobody is waited for.
        this.set(patch)
        const text = describeAction(msg.action, msg.by, msg.playback)
        if (text) this.event(text)
        break
      }
      case 'source':
        this.set({ source: msg.source })
        break
      case 'media':
        this.set({ media: normalizeMedia(msg.media) })
        break
      case 'title':
        this.set({ title: msg.title })
        break
      case 'subtitle_added':
        // Without who added it, the server found it for this episode.
        if (msg.by) this.notify('notice.subtitleAdded', { by: msg.by, name: msg.name })
        else this.notify('notice.subtitleFollowed', { name: msg.name })
        break
      case 'chat':
        this.pushFeed({ kind: 'chat', key: `c${msg.msg.id}`, msg: msg.msg })
        break
      case 'reaction': {
        const r: Reaction = { key: ++this.reactionKey, emoji: msg.emoji, nick: msg.nick }
        this.set({ reactions: [...this.state.reactions.slice(-30), r] })
        window.setTimeout(() => {
          this.set({ reactions: this.state.reactions.filter((x) => x.key !== r.key) })
        }, 3500)
        break
      }
      case 'request':
        this.set({ requests: [...this.state.requests.filter((r) => r.id !== msg.request.id), msg.request] })
        break
      case 'request_resolved': {
        const req = this.state.requests.find((r) => r.id === msg.id)
        this.set({ requests: this.state.requests.filter((r) => r.id !== msg.id) })
        // Gone: replaced by a newer request or answered elsewhere; no notice.
        if (req && !msg.gone && req.userId === this.state.you?.userId) {
          if (msg.expired) this.notify('notice.requestExpired')
          else this.notify(msg.accepted ? 'notice.requestAccepted' : 'notice.requestDeclined', { by: msg.by })
        }
        break
      }
      case 'you':
        // Without control only one's own request is left to follow.
        this.set({
          you: msg.you,
          requests:
            msg.you.role === 'viewer' ? this.state.requests.filter((r) => r.userId === msg.you.userId) : this.state.requests,
        })
        this.notify(roleNotice(msg.you.role))
        break
      case 'settings':
        this.set({ autoplayNext: msg.autoplayNext })
        break
      case 'error':
        {
          const n = errorNotice(msg.code, msg.message)
          this.notify(n.key, n.vars)
        }
        break
      case 'closed':
        this.set({ connection: 'closed', closedReason: msg.reason })
        this.close()
        break
    }
  }
}
export function formatTime(sec: number) {
  if (!Number.isFinite(sec) || sec < 0) sec = 0
  const s = Math.floor(sec % 60)
  const m = Math.floor((sec / 60) % 60)
  const h = Math.floor(sec / 3600)
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m)
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`
}

/** A member's nickname, or a localized placeholder for an empty one. */
export function displayNick(nick?: string) {
  return nick?.trim() ? nick : t('common.guest')
}

function describeAction(action: string | undefined, by: string | undefined, p: Playback): Localized | null {
  const vars = { by: by ? displayNick(by) : undefined, time: formatTime(p.pos) }
  switch (action) {
    case 'play':
      return { key: 'ev.play', vars }
    case 'pause':
      return { key: 'ev.pause', vars }
    case 'seek':
      return { key: 'ev.seek', vars }
    case 'buffering':
      return { key: 'ev.buffering', vars }
    case 'loading':
      return { key: 'ev.loading' }
    case 'skip':
      return { key: 'ev.skip', vars }
    case 'ended':
      return { key: 'ev.ended' }
    case 'select':
      return by ? { key: 'ev.selecting', vars } : null
  }
  return null
}

function roleNotice(role: Role): MessageKey {
  switch (role) {
    case 'owner':
      return 'notice.roleOwner'
    case 'controller':
      return 'notice.roleController'
    default:
      return 'notice.roleViewer'
  }
}

function errorNotice(code: string, message: string): Localized {
  switch (code) {
    case 'forbidden':
      return { key: 'notice.forbidden' }
    case 'rate_limited':
      return { key: 'notice.rateLimited' }
    default:
      return { key: 'notice.error', vars: { message } }
  }
}

function normalizeMedia(m: Media | null): Media | null {
  if (!m?.info) return m
  return { ...m, info: { ...m.info, audio: m.info.audio ?? [], subtitles: m.info.subtitles ?? [], fonts: m.info.fonts ?? [] } }
}

/** The source tab is named after what the torrent holds. */
export function sourceTabKey(state: RoomState): MessageKey {
  const videos = (state.source?.files ?? []).filter((f) => f.kind === 'video').length
  return videos > 1 ? 'room.tabEpisodes' : 'room.tabMovie'
}
